import { FormControl, FormField, FormItem, FormLabel } from "@/src/components/ui/form";
import { YachtForm } from "./yacht.schema";
import { useFieldArray, useFormContext } from "react-hook-form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/src/components/ui/select";
import { DateRangePicker } from "@/src/components/ui/date-range-picker";
import { Button } from "@/src/components/ui/button";
import { PlusIcon, Trash2Icon } from "lucide-react";
import { InputNumber } from "@/src/components/ui/input-number";
import { Card } from "@/src/components/wrappers/Card";
import { CurrencySelect } from "@/src/components/CurrencySelect";
import { extractFromMultiName } from "../../Locations/utils";
import { Combobox } from "@/src/components/ui/combobox";
import { fetch } from "@/src/lib/fetch";
import { Location } from "../../Locations/model";
import { RelationMultiInput } from "../../Locations/components/RelationMultiInput";
import { RadioGroup, RadioGroupItem } from "@/src/components/ui/radio-group";

export function LocationAndPricing() {
  const form = useFormContext<YachtForm>();
  const pricing = form.watch("locationAndPricing.pricing");
  const { fields: seasonalRateFields, append, remove } = useFieldArray({
    control: form.control,
    name: "locationAndPricing.pricing.prices",
  });
  const isSimplePricing = pricing.type === "simple";
  const pricingsLen = pricing.type === "seasonal" ? pricing.prices.length : 0;

  const loadLocations = (query: string) =>
    fetch<{ data: Location[] }>(`/api/locations/locations?pageSize=20&filter=${encodeURIComponent(query)}`).then(
      (r) => r.data,
    );

  const setSeasonalRates = () => {
    if (pricing.type === "simple") {
      form.setValue("locationAndPricing.pricing", {
        type: "seasonal",
        prices: [
          {
            locations: [pricing.location],
            price: pricing.price,
            per: pricing.per,
            currency: pricing.currency,
            vat: pricing.vat,
            apa: pricing.apa,
            dateRange: {
              startDate: `${new Date().getFullYear()}-01-01`, // start of year
              endDate: `${new Date().getFullYear()}-12-31`,
            },
          },
        ],
      });
    }
  };

  const setSimpleRate = () => {
    if (pricing.type === "seasonal") {
      const firstLocation = pricing.prices[0]?.locations[0];
      const firstRate = pricing.prices[0];

      form.setValue("locationAndPricing.pricing", {
        type: "simple",
        location: firstLocation,
        price: firstRate?.price ?? 0,
        currency: firstRate?.currency ?? "EUR",
        per: firstRate?.per ?? "day",
        vat: firstRate?.vat ?? 0,
        apa: firstRate?.apa ?? 0,
      });
    }
  };

  const removeRate = (index: number) => {
    if (pricing.type === "seasonal") {
      remove(index);
    }
  };

  return (
    <Card>
      <div className="flex flex-col items-stretch">
        <h3 className="text-lg font-semibold mb-4">Location &amp; pricing</h3>
        <h5 className="text-sm mb-6">Add location and price for a yacht.</h5>
        <div className="mb-6 flex flex-col gap-3">
          <span className="text-sm font-medium">Type</span>
          <RadioGroup
            value={pricing.type}
            onValueChange={(value) => {
              if (value === "simple") {
                setSimpleRate();
              } else {
                setSeasonalRates();
              }
            }}
            className="flex gap-6"
          >
            <label className="flex items-center gap-3">
              <RadioGroupItem value="simple" />
              <span>Single rate</span>
            </label>
            <label className="flex items-center gap-3">
              <RadioGroupItem value="seasonal" />
              <span>General rates</span>
            </label>
          </RadioGroup>
        </div>
        <div className={isSimplePricing ? "flex gap-12 items-stretch" : "flex"}>
          {isSimplePricing && (
            <>
              <div className="flex flex-col flex-1 gap-6">
                <h2 className="text-lg font-semibold">Location</h2>
                <div className="grid grid-cols-2 gap-4">
                  <FormField<YachtForm, `locationAndPricing.pricing.location`>
                    name="locationAndPricing.pricing.location"
                    render={({ field }) => (
                      <FormItem required>
                        <FormLabel>Location</FormLabel>
                        <FormControl>
                          <Combobox<Location>
                            className="w-full"
                            loadOptions={loadLocations}
                            value={field.value as any}
                            onChange={field.onChange}
                            renderOption={(r) => extractFromMultiName(r.names)}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>
              </div>
              <div className="w-0 border" />
            </>
          )}
          <div className={isSimplePricing ? "flex flex-col flex-1 gap-6" : "flex flex-col gap-6 w-full max-w-5xl"}>
            <h2 className="text-lg font-semibold">Pricing</h2>

            <div className="grid grid-cols-4 gap-4">
              <FormField<YachtForm, "locationAndPricing.securityDeposit.price">
                name="locationAndPricing.securityDeposit.price"
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormLabel>Security deposit</FormLabel>
                    <FormControl>
                      <InputNumber {...field} />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField<YachtForm, "locationAndPricing.securityDeposit.currency">
                name="locationAndPricing.securityDeposit.currency"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Currency</FormLabel>
                    <CurrencySelect {...field} />
                  </FormItem>
                )}
              />
            </div>
            {isSimplePricing && (
              <>
                <div className="grid grid-cols-4 gap-4">
                  <FormField<YachtForm, "locationAndPricing.pricing.price">
                    name="locationAndPricing.pricing.price"
                    render={({ field }) => (
                      <FormItem required className="col-span-2">
                        <FormLabel>Base price</FormLabel>
                        <FormControl>
                          <InputNumber {...field} />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  <FormField<YachtForm, "locationAndPricing.pricing.currency">
                    name="locationAndPricing.pricing.currency"
                    render={({ field }) => (
                      <FormItem required>
                        <FormLabel>Currency</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="EUR">EUR</SelectItem>
                            <SelectItem value="USD">USD</SelectItem>
                            <SelectItem value="GBP">GBP</SelectItem>
                          </SelectContent>
                        </Select>
                      </FormItem>
                    )}
                  />
                  <FormField<YachtForm, "locationAndPricing.pricing.per">
                    name="locationAndPricing.pricing.per"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Per</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="week">Week</SelectItem>
                            <SelectItem value="day">Day</SelectItem>
                            <SelectItem value="hour">Hour</SelectItem>
                          </SelectContent>
                        </Select>
                      </FormItem>
                    )}
                  />
                  <FormField<YachtForm, "locationAndPricing.pricing.vat">
                    name="locationAndPricing.pricing.vat"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>VAT %</FormLabel>
                        <FormControl>
                          <InputNumber {...field} />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  <FormField<YachtForm, "locationAndPricing.pricing.apa">
                    name="locationAndPricing.pricing.apa"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>APA %</FormLabel>
                        <FormControl>
                          <InputNumber {...field} />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>
              </>
            )}
            {!isSimplePricing && (
              <>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">General rates use the composite location as primary.</span>
                </div>
                {seasonalRateFields.map((fieldRow, i) => (
                  <div key={fieldRow.id} className="grid grid-cols-4 gap-4 border rounded-md border-slate-300 bg-blue-50 p-4">
                    <div className="col-span-full">
                      <RelationMultiInput
                        name={`locationAndPricing.pricing.prices.${i}.locations`}
                        label="Locations"
                        loadOptions={loadLocations}
                        renderOption={(value) => extractFromMultiName(value.names)}
                      />
                    </div>
                    <FormField<YachtForm, `locationAndPricing.pricing.prices.${number}.dateRange`>
                      name={`locationAndPricing.pricing.prices.${i}.dateRange`}
                      render={({ field }) => (
                        <FormItem required className="col-span-full">
                          <FormLabel>Date range</FormLabel>
                          <FormControl>
                            <DateRangePicker
                              className="w-full bg-white"
                              value={field.value}
                              onChange={field.onChange}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                    <FormField<YachtForm, `locationAndPricing.pricing.prices.${number}.price`>
                      name={`locationAndPricing.pricing.prices.${i}.price`}
                      render={({ field }) => (
                        <FormItem required className="col-span-2">
                          <FormLabel>Price</FormLabel>
                          <FormControl>
                            <InputNumber className="bg-white" {...field} />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                    <FormField<YachtForm, `locationAndPricing.pricing.prices.${number}.currency`>
                      name={`locationAndPricing.pricing.prices.${i}.currency`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>&nbsp;</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="EUR">EUR</SelectItem>
                              <SelectItem value="USD">USD</SelectItem>
                              <SelectItem value="GBP">GBP</SelectItem>
                            </SelectContent>
                          </Select>
                        </FormItem>
                      )}
                    />
                    <FormField<YachtForm, `locationAndPricing.pricing.prices.${number}.per`>
                      name={`locationAndPricing.pricing.prices.${i}.per`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Per</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="week">Week</SelectItem>
                              <SelectItem value="day">Day</SelectItem>
                              <SelectItem value="hour">Hour</SelectItem>
                            </SelectContent>
                          </Select>
                        </FormItem>
                      )}
                    />
                    <FormField<YachtForm, `locationAndPricing.pricing.prices.${number}.vat`>
                      name={`locationAndPricing.pricing.prices.${i}.vat`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>VAT %</FormLabel>
                          <FormControl>
                            <InputNumber className="bg-white" {...field} />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                    <FormField<YachtForm, `locationAndPricing.pricing.prices.${number}.apa`>
                      name={`locationAndPricing.pricing.prices.${i}.apa`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>APA %</FormLabel>
                          <FormControl>
                            <InputNumber className="bg-white" {...field} />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                    <div className="col-span-full flex justify-end">
                      <Button type="button" variant="ghost" onClick={() => removeRate(i)} disabled={pricingsLen <= 1}>
                        Remove rate <Trash2Icon size={16} />
                      </Button>
                    </div>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    const seedLocations =
                      pricing.prices[pricing.prices.length - 1]?.locations ?? pricing.prices[0]?.locations ?? [];
                    append({
                      locations: [...seedLocations] as any,
                      dateRange: { startDate: "", endDate: "" },
                      price: 0,
                      currency: "EUR",
                      per: "week",
                      vat: pricing.prices[pricing.prices.length - 1]?.vat ?? 0,
                      apa: pricing.prices[pricing.prices.length - 1]?.apa ?? 0,
                    });
                  }}
                >
                  Add another rate <PlusIcon size={16} />
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}
